import React, { Children } from 'react';
import PropTypes from 'prop-types';
import { createMemoStyles, defaultBaseline, defaultFonts, safeRest, withSpacing, clsx } from '@wds/utils';
import { useTheme } from 'react-jss';
function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;
  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }
  return target;
}
function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};
  var target = _objectWithoutPropertiesLoose(source, excluded);
  var key, i;
  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }
  return target;
}
function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }
  return obj;
}
function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    if (enumerableOnly) {
      symbols = symbols.filter(function (sym) {
        return Object.getOwnPropertyDescriptor(object, sym).enumerable;
      });
    }
    keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};
    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }
  return target;
}
var useStylesGrid = createMemoStyles(function (theme) {
  var _objectSpread2, _wrenchGrid, _wrenchGridItem;
  var breakpoints = theme.breakpoints,
    spacing = theme.spacing;
  var grid = {
    bleed: {
      xs: spacing[4],
      sm: spacing[6],
      md: spacing[6],
      lg: spacing[6],
      xl: spacing[6]
    },
    gutter: {
      xs: spacing[4],
      sm: spacing[5],
      md: spacing[5],
      lg: spacing[5],
      xl: spacing[5]
    },
    maxWidth: _objectSpread(_objectSpread({}, breakpoints), {}, {
      xl: 1400
    }),
    columns: 12
  };
  var gridGutter = function gridGutter(size) {
    return size ? grid.gutter[size] / -2 : 0;
  };
  var width = function width(size) {
    return size ? "".concat(100 * (size / grid.columns), "%") : 0;
  };
  var gridGutterItem = function gridGutterItem(size) {
    return size ? grid.gutter[size] / 2 : 0;
  };
  return {
    wrenchGridContainer: _objectSpread(_objectSpread(_objectSpread({}, defaultBaseline), defaultFonts(theme)), {}, (_objectSpread2 = {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      marginRight: 'auto',
      marginLeft: 'auto'
    }, _defineProperty(_objectSpread2, "@media (min-width: ".concat(breakpoints.xs, "px)"), {
      paddingLeft: function paddingLeft(_ref) {
        var noBleeds = _ref.noBleeds;
        return !noBleeds && grid.bleed.xs;
      },
      paddingRight: function paddingRight(_ref2) {
        var noBleeds = _ref2.noBleeds;
        return !noBleeds && grid.bleed.xs;
      }
    }), _defineProperty(_objectSpread2, "@media (min-width: ".concat(breakpoints.sm, "px)"), {
      maxWidth: function maxWidth(_ref3) {
        var fluid = _ref3.fluid;
        return !fluid && grid.maxWidth.sm;
      },
      paddingLeft: function paddingLeft(_ref4) {
        var noBleeds = _ref4.noBleeds;
        return !noBleeds && grid.bleed.sm;
      },
      paddingRight: function paddingRight(_ref5) {
        var noBleeds = _ref5.noBleeds;
        return !noBleeds && grid.bleed.sm;
      }
    }), _defineProperty(_objectSpread2, "@media (min-width: ".concat(breakpoints.md, "px)"), {
      maxWidth: function maxWidth(_ref6) {
        var fluid = _ref6.fluid;
        return !fluid && grid.maxWidth.md;
      },
      paddingLeft: function paddingLeft(_ref7) {
        var noBleeds = _ref7.noBleeds;
        return !noBleeds && grid.bleed.md;
      },
      paddingRight: function paddingRight(_ref8) {
        var noBleeds = _ref8.noBleeds;
        return !noBleeds && grid.bleed.md;
      }
    }), _defineProperty(_objectSpread2, "@media (min-width: ".concat(breakpoints.lg, "px)"), {
      maxWidth: function maxWidth(_ref9) {
        var fluid = _ref9.fluid;
        return !fluid && grid.maxWidth.lg;
      },
      paddingLeft: function paddingLeft(_ref10) {
        var noBleeds = _ref10.noBleeds;
        return !noBleeds && grid.bleed.lg;
      },
      paddingRight: function paddingRight(_ref11) {
        var noBleeds = _ref11.noBleeds;
        return !noBleeds && grid.bleed.lg;
      }
    }), _defineProperty(_objectSpread2, "@media (min-width: ".concat(breakpoints.xl, "px)"), {
      maxWidth: function maxWidth(_ref12) {
        var fluid = _ref12.fluid;
        return !fluid && grid.maxWidth.xl;
      },
      paddingLeft: function paddingLeft(_ref13) {
        var noBleeds = _ref13.noBleeds;
        return !noBleeds && grid.bleed.xl;
      },
      paddingRight: function paddingRight(_ref14) {
        var noBleeds = _ref14.noBleeds;
        return !noBleeds && grid.bleed.xl;
      }
    }), _objectSpread2)),
    wrenchGrid: (_wrenchGrid = {
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'row',
      flexWrap: 'wrap'
    }, _defineProperty(_wrenchGrid, "@media (min-width: ".concat(breakpoints.xs, "px)"), {
      paddingLeft: function paddingLeft(_ref15) {
        var noGutters = _ref15.noGutters;
        return !noGutters && gridGutter('xs');
      },
      paddingRight: function paddingRight(_ref16) {
        var noGutters = _ref16.noGutters;
        return !noGutters && gridGutter('xs');
      }
    }), _defineProperty(_wrenchGrid, "@media (min-width: ".concat(breakpoints.sm, "px)"), {
      paddingLeft: function paddingLeft(_ref17) {
        var noGutters = _ref17.noGutters;
        return !noGutters && gridGutter('sm');
      },
      paddingRight: function paddingRight(_ref18) {
        var noGutters = _ref18.noGutters;
        return !noGutters && gridGutter('sm');
      }
    }), _defineProperty(_wrenchGrid, "@media (min-width: ".concat(breakpoints.md, "px)"), {
      paddingLeft: function paddingLeft(_ref19) {
        var noGutters = _ref19.noGutters;
        return !noGutters && gridGutter('md');
      },
      paddingRight: function paddingRight(_ref20) {
        var noGutters = _ref20.noGutters;
        return !noGutters && gridGutter('md');
      }
    }), _defineProperty(_wrenchGrid, "@media (min-width: ".concat(breakpoints.lg, "px)"), {
      paddingLeft: function paddingLeft(_ref21) {
        var noGutters = _ref21.noGutters;
        return !noGutters && gridGutter('lg');
      },
      paddingRight: function paddingRight(_ref22) {
        var noGutters = _ref22.noGutters;
        return !noGutters && gridGutter('lg');
      }
    }), _defineProperty(_wrenchGrid, "@media (min-width: ".concat(breakpoints.xl, "px)"), {
      paddingLeft: function paddingLeft(_ref23) {
        var noGutters = _ref23.noGutters;
        return !noGutters && gridGutter('xl');
      },
      paddingRight: function paddingRight(_ref24) {
        var noGutters = _ref24.noGutters;
        return !noGutters && gridGutter('xl');
      }
    }), _wrenchGrid),
    wrenchGridItem: (_wrenchGridItem = {
      boxSizing: 'border-box',
      flexGrow: '1',
      flexBasis: '0',
      maxWidth: '100%'
    }, _defineProperty(_wrenchGridItem, "@media (min-width: ".concat(breakpoints.xs, "px)"), {
      flex: function flex(_ref25) {
        var xs = _ref25.xs;
        return xs && "0 0 ".concat(width(xs));
      },
      maxWidth: function maxWidth(_ref26) {
        var xs = _ref26.xs;
        return xs && width(xs);
      },
      order: function order(_ref27) {
        var xsOrder = _ref27.xsOrder;
        return xsOrder;
      },
      paddingLeft: function paddingLeft(_ref28) {
        var noGutters = _ref28.noGutters;
        return !noGutters && gridGutterItem('xs');
      },
      paddingRight: function paddingRight(_ref29) {
        var noGutters = _ref29.noGutters;
        return !noGutters && gridGutterItem('xs');
      }
    }), _defineProperty(_wrenchGridItem, "@media (min-width: ".concat(breakpoints.sm, "px)"), {
      flex: function flex(_ref30) {
        var sm = _ref30.sm;
        return sm && "0 0 ".concat(width(sm));
      },
      maxWidth: function maxWidth(_ref31) {
        var sm = _ref31.sm;
        return sm && width(sm);
      },
      order: function order(_ref32) {
        var smOrder = _ref32.smOrder;
        return smOrder;
      },
      paddingLeft: function paddingLeft(_ref33) {
        var noGutters = _ref33.noGutters;
        return !noGutters && gridGutterItem('sm');
      },
      paddingRight: function paddingRight(_ref34) {
        var noGutters = _ref34.noGutters;
        return !noGutters && gridGutterItem('sm');
      }
    }), _defineProperty(_wrenchGridItem, "@media (min-width: ".concat(breakpoints.md, "px)"), {
      flex: function flex(_ref35) {
        var md = _ref35.md;
        return md && "0 0 ".concat(width(md));
      },
      maxWidth: function maxWidth(_ref36) {
        var md = _ref36.md;
        return md && width(md);
      },
      order: function order(_ref37) {
        var mdOrder = _ref37.mdOrder;
        return mdOrder;
      },
      paddingLeft: function paddingLeft(_ref38) {
        var noGutters = _ref38.noGutters;
        return !noGutters && gridGutterItem('md');
      },
      paddingRight: function paddingRight(_ref39) {
        var noGutters = _ref39.noGutters;
        return !noGutters && gridGutterItem('md');
      }
    }), _defineProperty(_wrenchGridItem, "@media (min-width: ".concat(breakpoints.lg, "px)"), {
      flex: function flex(_ref40) {
        var lg = _ref40.lg;
        return lg && "0 0 ".concat(width(lg));
      },
      maxWidth: function maxWidth(_ref41) {
        var lg = _ref41.lg;
        return lg && width(lg);
      },
      order: function order(_ref42) {
        var lgOrder = _ref42.lgOrder;
        return lgOrder;
      },
      paddingLeft: function paddingLeft(_ref43) {
        var noGutters = _ref43.noGutters;
        return !noGutters && gridGutterItem('lg');
      },
      paddingRight: function paddingRight(_ref44) {
        var noGutters = _ref44.noGutters;
        return !noGutters && gridGutterItem('lg');
      }
    }), _defineProperty(_wrenchGridItem, "@media (min-width: ".concat(breakpoints.xl, "px)"), {
      flex: function flex(_ref45) {
        var xl = _ref45.xl;
        return xl && "0 0 ".concat(width(xl));
      },
      maxWidth: function maxWidth(_ref46) {
        var xl = _ref46.xl;
        return xl && width(xl);
      },
      order: function order(_ref47) {
        var xlOrder = _ref47.xlOrder;
        return xlOrder;
      },
      paddingLeft: function paddingLeft(_ref48) {
        var noGutters = _ref48.noGutters;
        return !noGutters && gridGutterItem('xl');
      },
      paddingRight: function paddingRight(_ref49) {
        var noGutters = _ref49.noGutters;
        return !noGutters && gridGutterItem('xl');
      }
    }), _wrenchGridItem)
  };
});
var _excluded$1 = ["children", "xs", "sm", "md", "lg", "xl", "noGutters", "xsOrder", "smOrder", "mdOrder", "lgOrder", "xlOrder"];
var gridColumns = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
var GridItem = function GridItem(_ref) {
  var children = _ref.children,
    xs = _ref.xs,
    sm = _ref.sm,
    md = _ref.md,
    lg = _ref.lg,
    xl = _ref.xl,
    noGutters = _ref.noGutters,
    xsOrder = _ref.xsOrder,
    smOrder = _ref.smOrder,
    mdOrder = _ref.mdOrder,
    lgOrder = _ref.lgOrder,
    xlOrder = _ref.xlOrder,
    rest = _objectWithoutProperties(_ref, _excluded$1);
  var theme = useTheme();
  var _useStylesGridItem = useStylesGrid({
      xs: xs,
      sm: sm,
      md: md,
      lg: lg,
      xl: xl,
      noGutters: noGutters,
      xsOrder: xsOrder,
      smOrder: smOrder,
      mdOrder: mdOrder,
      lgOrder: lgOrder,
      xlOrder: xlOrder,
      theme: theme
    }),
    wrenchGridItem = _useStylesGridItem.wrenchGridItem;
  return /*#__PURE__*/React.createElement("div", Object.assign({
    className: wrenchGridItem
  }, safeRest(rest)), children);
};
GridItem.displayName = "GridItem";
GridItem.propTypes = {
  children: PropTypes.node.isRequired,
  /**
   * Amount of space items take up at xs breakpoint and above.
   */
  xs: PropTypes.oneOf(gridColumns),
  /**
   * Amount of space items take up at sm breakpoint and above.
   */
  sm: PropTypes.oneOf(gridColumns),
  /**
   * Amount of space items take up at md breakpoint and above.
   */
  md: PropTypes.oneOf(gridColumns),
  /**
   * Amount of space items take up at lg breakpoint and above.
   */
  lg: PropTypes.oneOf(gridColumns),
  /**
   * Amount of space items take up at xl breakpoint and above.
   */
  xl: PropTypes.oneOf(gridColumns),
  /**
   * No gutters flag
   */
  noGutters: PropTypes.bool,
  /**
   * Order of item at xs breakpoint and above.
   */
  xsOrder: PropTypes.number,
  /**
   * Order of item at sm breakpoint and above.
   */
  smOrder: PropTypes.number,
  /**
   * Order of item at md breakpoint and above.
   */
  mdOrder: PropTypes.number,
  /**
   * Order of item at lg breakpoint and above.
   */
  lgOrder: PropTypes.number,
  /**
   * Order of item at xl breakpoint and above.
   */
  xlOrder: PropTypes.number
};
GridItem.defaultProps = {
  xs: undefined,
  sm: undefined,
  md: undefined,
  lg: undefined,
  xl: undefined,
  noGutters: undefined,
  xsOrder: undefined,
  smOrder: undefined,
  mdOrder: undefined,
  lgOrder: undefined,
  xlOrder: undefined
};
GridItem.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "GridItem",
  "props": {
    "xs": {
      "defaultValue": {
        "value": "undefined",
        "computed": true
      },
      "type": {
        "name": "enum",
        "value": [{
          "value": "1",
          "computed": false
        }, {
          "value": "2",
          "computed": false
        }, {
          "value": "3",
          "computed": false
        }, {
          "value": "4",
          "computed": false
        }, {
          "value": "5",
          "computed": false
        }, {
          "value": "6",
          "computed": false
        }, {
          "value": "7",
          "computed": false
        }, {
          "value": "8",
          "computed": false
        }, {
          "value": "9",
          "computed": false
        }, {
          "value": "10",
          "computed": false
        }, {
          "value": "11",
          "computed": false
        }, {
          "value": "12",
          "computed": false
        }]
      },
      "required": false,
      "description": "Amount of space items take up at xs breakpoint and above."
    },
    "sm": {
      "defaultValue": {
        "value": "undefined",
        "computed": true
      },
      "type": {
        "name": "enum",
        "value": [{
          "value": "1",
          "computed": false
        }, {
          "value": "2",
          "computed": false
        }, {
          "value": "3",
          "computed": false
        }, {
          "value": "4",
          "computed": false
        }, {
          "value": "5",
          "computed": false
        }, {
          "value": "6",
          "computed": false
        }, {
          "value": "7",
          "computed": false
        }, {
          "value": "8",
          "computed": false
        }, {
          "value": "9",
          "computed": false
        }, {
          "value": "10",
          "computed": false
        }, {
          "value": "11",
          "computed": false
        }, {
          "value": "12",
          "computed": false
        }]
      },
      "required": false,
      "description": "Amount of space items take up at sm breakpoint and above."
    },
    "md": {
      "defaultValue": {
        "value": "undefined",
        "computed": true
      },
      "type": {
        "name": "enum",
        "value": [{
          "value": "1",
          "computed": false
        }, {
          "value": "2",
          "computed": false
        }, {
          "value": "3",
          "computed": false
        }, {
          "value": "4",
          "computed": false
        }, {
          "value": "5",
          "computed": false
        }, {
          "value": "6",
          "computed": false
        }, {
          "value": "7",
          "computed": false
        }, {
          "value": "8",
          "computed": false
        }, {
          "value": "9",
          "computed": false
        }, {
          "value": "10",
          "computed": false
        }, {
          "value": "11",
          "computed": false
        }, {
          "value": "12",
          "computed": false
        }]
      },
      "required": false,
      "description": "Amount of space items take up at md breakpoint and above."
    },
    "lg": {
      "defaultValue": {
        "value": "undefined",
        "computed": true
      },
      "type": {
        "name": "enum",
        "value": [{
          "value": "1",
          "computed": false
        }, {
          "value": "2",
          "computed": false
        }, {
          "value": "3",
          "computed": false
        }, {
          "value": "4",
          "computed": false
        }, {
          "value": "5",
          "computed": false
        }, {
          "value": "6",
          "computed": false
        }, {
          "value": "7",
          "computed": false
        }, {
          "value": "8",
          "computed": false
        }, {
          "value": "9",
          "computed": false
        }, {
          "value": "10",
          "computed": false
        }, {
          "value": "11",
          "computed": false
        }, {
          "value": "12",
          "computed": false
        }]
      },
      "required": false,
      "description": "Amount of space items take up at lg breakpoint and above."
    },
    "xl": {
      "defaultValue": {
        "value": "undefined",
        "computed": true
      },
      "type": {
        "name": "enum",
        "value": [{
          "value": "1",
          "computed": false
        }, {
          "value": "2",
          "computed": false
        }, {
          "value": "3",
          "computed": false
        }, {
          "value": "4",
          "computed": false
        }, {
          "value": "5",
          "computed": false
        }, {
          "value": "6",
          "computed": false
        }, {
          "value": "7",
          "computed": false
        }, {
          "value": "8",
          "computed": false
        }, {
          "value": "9",
          "computed": false
        }, {
          "value": "10",
          "computed": false
        }, {
          "value": "11",
          "computed": false
        }, {
          "value": "12",
          "computed": false
        }]
      },
      "required": false,
      "description": "Amount of space items take up at xl breakpoint and above."
    },
    "noGutters": {
      "defaultValue": {
        "value": "undefined",
        "computed": true
      },
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": "No gutters flag"
    },
    "xsOrder": {
      "defaultValue": {
        "value": "undefined",
        "computed": true
      },
      "type": {
        "name": "number"
      },
      "required": false,
      "description": "Order of item at xs breakpoint and above."
    },
    "smOrder": {
      "defaultValue": {
        "value": "undefined",
        "computed": true
      },
      "type": {
        "name": "number"
      },
      "required": false,
      "description": "Order of item at sm breakpoint and above."
    },
    "mdOrder": {
      "defaultValue": {
        "value": "undefined",
        "computed": true
      },
      "type": {
        "name": "number"
      },
      "required": false,
      "description": "Order of item at md breakpoint and above."
    },
    "lgOrder": {
      "defaultValue": {
        "value": "undefined",
        "computed": true
      },
      "type": {
        "name": "number"
      },
      "required": false,
      "description": "Order of item at lg breakpoint and above."
    },
    "xlOrder": {
      "defaultValue": {
        "value": "undefined",
        "computed": true
      },
      "type": {
        "name": "number"
      },
      "required": false,
      "description": "Order of item at xl breakpoint and above."
    },
    "children": {
      "type": {
        "name": "node"
      },
      "required": true,
      "description": ""
    }
  }
};
var _excluded = ["children", "noGutters", "noBleeds", "fluid", "spacingClass"];
var Grid = function Grid(_ref) {
  var children = _ref.children,
    noGutters = _ref.noGutters,
    noBleeds = _ref.noBleeds,
    fluid = _ref.fluid,
    spacingClass = _ref.spacingClass,
    rest = _objectWithoutProperties(_ref, _excluded);
  var theme = useTheme();
  var _useStylesGrid = useStylesGrid({
      noGutters: noGutters,
      noBleeds: noBleeds,
      fluid: fluid,
      theme: theme
    }),
    wrenchGrid = _useStylesGrid.wrenchGrid,
    wrenchGridContainer = _useStylesGrid.wrenchGridContainer;
  var gridItems = Children.map(children, function (child) {
    return /*#__PURE__*/React.cloneElement(child, {
      noGutters: noGutters
    });
  });
  var wrenchGridWrapper = clsx(wrenchGridContainer, spacingClass);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", Object.assign({
    className: wrenchGridWrapper
  }, safeRest(rest)), /*#__PURE__*/React.createElement("div", {
    className: wrenchGrid
  }, gridItems)));
};
Grid.propTypes = {
  /**
   * Should be Grid.Item Component
   */
  children: PropTypes.node.isRequired,
  /**
   * Removes maxwidth from grid container.
   */
  fluid: PropTypes.bool,
  /**
   * Enable gaps on the left anf right of the grid.
   */
  noBleeds: PropTypes.bool,
  /**
   * Enable gutters between child elements in the grid.
   */
  noGutters: PropTypes.bool,
  /**
   * interior with spacing prop
   */
  spacingClass: PropTypes.string
};
Grid.defaultProps = {
  fluid: false,
  noBleeds: false,
  noGutters: false,
  spacingClass: undefined
};
var GridWithSpacing = withSpacing(Grid);
GridWithSpacing.Item = GridItem;
Grid.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "Grid",
  "props": {
    "fluid": {
      "defaultValue": {
        "value": "false",
        "computed": false
      },
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": "Removes maxwidth from grid container."
    },
    "noBleeds": {
      "defaultValue": {
        "value": "false",
        "computed": false
      },
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": "Enable gaps on the left anf right of the grid."
    },
    "noGutters": {
      "defaultValue": {
        "value": "false",
        "computed": false
      },
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": "Enable gutters between child elements in the grid."
    },
    "spacingClass": {
      "defaultValue": {
        "value": "undefined",
        "computed": true
      },
      "type": {
        "name": "string"
      },
      "required": false,
      "description": "interior with spacing prop"
    },
    "children": {
      "type": {
        "name": "node"
      },
      "required": true,
      "description": "Should be Grid.Item Component"
    }
  }
};
export { GridWithSpacing as Grid };